import { useEffect, useMemo, useRef, useState } from "react";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { reportsApi } from "../../services/apiClient";
import { formatDateTimeFr } from "../../shared/dateFormat";
import "./PageReportsList.css";
import {
  ProofModal,
  isReportsListDeepLinkPage,
  saveBlob,
  signatureMatchesCurrentUser,
  statusPill,
} from "./reportShared";

export default function PageReportsList({ currentUser, onToast, onOpenGenerationPage }) {
  const [items, setItems] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remindingId, setRemindingId] = useState(null);
  const [proofDetails, setProofDetails] = useState(null);
  const [proofReportId, setProofReportId] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null);
  const [archiveCandidate, setArchiveCandidate] = useState(null);
  const [archivingId, setArchivingId] = useState(null);

  const deepLinkRef = useRef(
    (() => {
      const params = new URLSearchParams(window.location.search);
      const reportId = Number(params.get("reportId") || 0);
      const pageParam = params.get("page");
      return reportId > 0 && isReportsListDeepLinkPage(pageParam)
        ? {
            reportId,
            openProof: params.get("openProof") === "1" || params.has("reportId"),
            focusSign: params.get("focus") === "sign",
          }
        : null;
    })()
  );
  const pdfBlobUrlRef = useRef(null);

  const revokePdfBlobUrl = () => {
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = null;
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const data = await reportsApi.list({ archived: showArchived });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setReportTypes(Array.isArray(data?.types) ? data.types : []);
    } catch (error) {
      onToast?.("error", error.message || "Impossible de charger les rapports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(
    () => () => {
      revokePdfBlobUrl();
    },
    []
  );

  const typesForUi = useMemo(
    () =>
      reportTypes.length > 0
        ? reportTypes
        : [
            {
              type: "raci",
              label: "RACI RGPD – Rôles & responsabilités",
              category: "Gouvernance",
              periodicity: "Selon votre organisation (souvent annuel ou à tout changement majeur).",
              nomenclaturePrefix: "G1",
            },
          ],
    [reportTypes]
  );

  const filteredItems = useMemo(() => {
    if (!filterType) return items;
    return items.filter((item) => item.reportType === filterType);
  }, [items, filterType]);

  const stats = useMemo(() => ({
    total: filteredItems.length,
    verified: filteredItems.filter((item) => item.status === "sealed").length,
    pending: filteredItems.filter((item) => item.status === "pending_signatures").length,
  }), [filteredItems]);

  const handleDownloadPdf = async (item) => {
    try {
      const { blob, filename } = await reportsApi.downloadPdf(item.id, {
        preferredFileName: item.downloadFileName,
      });
      saveBlob(blob, filename);
    } catch (error) {
      onToast?.("error", error.message || "Téléchargement impossible");
    }
  };

  const handleOpenProof = async (id, options = {}) => {
    const { focusSign = false } = options;
    try {
      const data = await reportsApi.proof(id);
      setProofReportId(id);
      setProofDetails(data);
      if (focusSign) {
        window.setTimeout(() => {
          document.getElementById("report-sign-pad-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    } catch (error) {
      onToast?.("error", error.message || "Impossible de charger la preuve");
    }
  };

  const handleRemind = async (id) => {
    setRemindingId(id);
    try {
      const data = await reportsApi.remind(id);
      onToast?.("success", data?.message || "Relance envoyee");
    } catch (error) {
      onToast?.("error", error.message || "Impossible d'envoyer la relance");
    } finally {
      setRemindingId(null);
    }
  };

  const handleViewReportPdf = async (item) => {
    revokePdfBlobUrl();
    setPdfViewer({ id: item.id, title: item.title, url: null, loading: true, error: null });
    try {
      const { blob } = await reportsApi.downloadPdf(item.id, {
        preferredFileName: item.downloadFileName,
      });
      const url = URL.createObjectURL(blob);
      pdfBlobUrlRef.current = url;
      setPdfViewer({ id: item.id, title: item.title || `Rapport #${item.id}`, url, loading: false, error: null });
    } catch (error) {
      onToast?.("error", error.message || "Impossible d'afficher le PDF");
      setPdfViewer((v) =>
        v && v.id === item.id ? { ...v, loading: false, error: error.message || "Erreur de chargement" } : v
      );
    }
  };

  const handleClosePdfViewer = () => {
    revokePdfBlobUrl();
    setPdfViewer(null);
  };

  const handleArchiveConfirmed = async () => {
    if (!archiveCandidate) return;
    const idArchived = archiveCandidate.id;
    setArchivingId(idArchived);
    try {
      await reportsApi.archive(idArchived);
      onToast?.("success", "Rapport archivé.");
      if (pdfViewer?.id === idArchived) handleClosePdfViewer();
      setArchiveCandidate(null);
      await loadReports();
    } catch (error) {
      onToast?.("error", error.message || "Archivage impossible");
    } finally {
      setArchivingId(null);
    }
  };

  useEffect(() => {
    if (loading || !deepLinkRef.current?.openProof || proofReportId === deepLinkRef.current.reportId) return;
    handleOpenProof(deepLinkRef.current.reportId, { focusSign: Boolean(deepLinkRef.current.focusSign) });
    // Ouverture unique depuis un lien email
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, proofReportId]);

  const handleCloseProof = () => {
    setProofDetails(null);
    setProofReportId(null);

    const params = new URLSearchParams(window.location.search);
    if (params.has("reportId") || params.has("openProof") || params.has("focus")) {
      params.delete("reportId");
      params.delete("openProof");
      params.delete("focus");
      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", nextUrl);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Rapports PDF horodatés"
        subtitle={
          <>
            Liste des rapports stockés, signatures et preuves TSA. Les rapports d’incident PGIS se créent depuis la page <strong>Incidents</strong>.
            {onOpenGenerationPage ? (
              <>
                {" "}
                <button
                  type="button"
                  className="page-shell__btn page-shell__btn--ghost"
                  onClick={onOpenGenerationPage}
                  style={{ padding: 0, border: "none", background: "none", textDecoration: "underline", fontWeight: 700 }}
                >
                  Planification et génération manuelle
                </button>
              </>
            ) : null}
          </>
        }
        actions={
          <div className="reports-header-toolbar">
            <button
              type="button"
              className={`page-shell__btn ${showArchived ? "page-shell__btn--primary" : "page-shell__btn--ghost"}`}
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "↩ Rapports actifs" : "Rapports archivés"}
            </button>
            <label className="reports-filter-field">
              <span className="reports-filter-field__label">Type de rapport</span>
              <select
                className="reports-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                aria-label="Type de rapport"
              >
                <option value="">Tous les types</option>
                {typesForUi.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.label || t.type}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="page-shell__btn page-shell__btn--ghost" onClick={loadReports}>
              Actualiser la liste
            </button>
          </div>
        }
      />

      <PageContent>
        <div className="reports-page">
        {showArchived ? (
          <div style={{ marginBottom: 14, padding: "10px 14px", background: "#f1f5f9", borderRadius: 10, border: "1px solid #cbd5e1", fontSize: "clamp(12px, 2vw, 13px)", color: "#475569" }}>
            Affichage des <strong style={{ color: "#0f172a" }}>rapports archivés</strong> uniquement — hors vue par défaut.
          </div>
        ) : null}
        {filterType ? (
          <div style={{ marginBottom: 14, fontSize: "clamp(12px, 2vw, 13px)", color: "#64748b" }}>
            Filtre actif : <strong style={{ color: "#0f172a" }}>{typesForUi.find((t) => t.type === filterType)?.label || filterType}</strong> — {filteredItems.length} rapport(s)
          </div>
        ) : null}

        <div className="reports-stats">
          {(showArchived
            ? [
                { label: "Archives affichées", val: stats.total, tone: "slate" },
                { label: "En attente signatures", val: stats.pending, tone: "amber" },
                { label: "Scellés TSA", val: stats.verified, tone: "green" },
              ]
            : [
                { label: "Rapports affichés", val: stats.total, tone: "blue" },
                { label: "En attente de signatures", val: stats.pending, tone: "amber" },
                { label: "Scellés TSA", val: stats.verified, tone: "green" },
              ]
          ).map((item) => (
            <div key={item.label} className={`reports-stats__card reports-stats__card--${item.tone}`}>
              <div className="reports-stats__value">{item.val}</div>
              <div className="reports-stats__label">{item.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 28, textAlign: "center", color: "#64748b" }}>Chargement des rapports…</div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", color: "#64748b" }}>
            {items.length === 0
              ? showArchived
                ? "Aucun rapport archivé pour le moment."
                : "Aucun rapport horodaté pour le moment. Utilisez la page de génération pour créer un PDF ou attendez la planification automatique."
              : "Aucun rapport pour ce type. Modifiez le filtre ou générez un nouveau rapport."}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {filteredItems.map((item) => (
              <div key={item.id} className="reports-card">
                <div className="reports-card__layout">
                  <div className="reports-card__body">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ padding: "4px 10px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800 }}>{item.reportType?.toUpperCase()}</span>
                      {item.nomenclaturePrefix ? (
                        <span
                          title="Préfixe nomenclature (nom du fichier téléchargé)"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 8,
                            background: "#e0f2fe",
                            border: "1px solid #7dd3fc",
                            color: "#0369a1",
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.04em",
                          }}
                        >
                          {item.nomenclaturePrefix}
                        </span>
                      ) : null}
                      <span style={{ padding: "4px 10px", borderRadius: 999, background: statusPill(item.status).bg, color: statusPill(item.status).color, fontSize: 11, fontWeight: 800 }}>{item.statusLabel}</span>
                      {item.isArchived ? (
                        <span style={{ padding: "4px 10px", borderRadius: 999, background: "#e2e8f0", color: "#475569", fontSize: 11, fontWeight: 800 }}>Archivé</span>
                      ) : null}
                      <span style={{ padding: "4px 10px", borderRadius: 999, background: item.proofStatus === "verified" ? "#dcfce7" : "#e2e8f0", color: item.proofStatus === "verified" ? "#166534" : "#475569", fontSize: 11, fontWeight: 800 }}>{item.proofStatus}</span>
                      <span style={{ color: "#64748b", fontSize: 12 }}>{item.versionLabel}</span>
                    </div>
                    <div className="reports-card__title">{item.title}</div>
                    <div className="reports-card__meta">
                      <div style={{ color: "#64748b", fontWeight: 700 }}>Créé le</div><div>{item.createdAt ? formatDateTimeFr(item.createdAt) : "—"}</div>
                      <div style={{ color: "#64748b", fontWeight: 700 }}>Horodaté TSA</div><div>{item.tsaGeneratedAt ? formatDateTimeFr(item.tsaGeneratedAt) : "—"}</div>
                      <div style={{ color: "#64748b", fontWeight: 700 }}>SHA-256</div><div style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}>{item.sha256}</div>
                      {item.archivedAt ? (
                        <>
                          <div style={{ color: "#64748b", fontWeight: 700 }}>Archivé le</div>
                          <div>{formatDateTimeFr(item.archivedAt)}</div>
                        </>
                      ) : null}
                    </div>
                    <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                      {(item.signatures || []).map((signature) => {
                        const isMine = signatureMatchesCurrentUser(signature, currentUser);
                        return (
                          <div key={signature.id || signature.role} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 12, background: isMine ? "#eff6ff" : "#f8fafc", border: "1px solid #e5e7eb" }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{signature.role}</div>
                              <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                                {signature.expectedName || "Compte à associer"}{signature.expectedFunction ? ` - ${signature.expectedFunction}` : ""}
                              </div>
                              {signature.expectedEmail && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{signature.expectedEmail}</div>}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: signature.status === "signed" ? "#166534" : "#92400e" }}>
                                {signature.status === "signed" ? "Signé" : "En attente"}
                              </div>
                              {signature.signedAt && <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{formatDateTimeFr(signature.signedAt)}</div>}
                              {signature.signedByEmail && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{signature.signedByEmail}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="reports-card__actions">
                    <button
                      type="button"
                      onClick={() => handleViewReportPdf(item)}
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #1d4ed8", background: "#fff", color: "#1d4ed8", fontWeight: 700, cursor: "pointer" }}
                    >
                      Voir le rapport
                    </button>
                    <button type="button" onClick={() => handleDownloadPdf(item)} style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: "#1d4ed8", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Télécharger PDF</button>
                    {item.canCurrentUserSign && !item.isArchived ? (
                      <button
                        type="button"
                        title="Ouvre la fenêtre avec le pavé de signature manuscrite"
                        onClick={() => handleOpenProof(item.id, { focusSign: true })}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}
                      >
                        Signer
                      </button>
                    ) : null}
                    {item.status === "pending_signatures" && !item.isArchived && item.pendingSignatureCount > 0 ? (
                      <button type="button" onClick={() => handleRemind(item.id)} disabled={remindingId === item.id} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff8e1", color: "#92400e", fontWeight: 700, cursor: "pointer", opacity: remindingId === item.id ? 0.7 : 1 }}>
                        {remindingId === item.id ? "Relance..." : "Relancer les non-signés"}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => handleOpenProof(item.id)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", fontWeight: 700, cursor: "pointer" }}>
                      {item.status === "sealed" ? "Voir la preuve" : "Voir le workflow"}
                    </button>
                    {!item.isArchived && !showArchived ? (
                      <button
                        type="button"
                        onClick={() => setArchiveCandidate(item)}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#64748b", fontWeight: 700, cursor: "pointer" }}
                      >
                        Archiver
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pdfViewer ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-pdf-preview-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => e.target === e.currentTarget && handleClosePdfViewer()}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 960,
              width: "100%",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h2 id="report-pdf-preview-title" style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
                {pdfViewer.title}
              </h2>
              <button
                type="button"
                onClick={handleClosePdfViewer}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#f8fafc",
                  color: "#334155",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 400, background: "#1e293b", position: "relative" }}>
              {pdfViewer.loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontWeight: 600 }}>Chargement du PDF…</div>
              ) : null}
              {!pdfViewer.loading && pdfViewer.error ? (
                <div style={{ padding: 40, textAlign: "center", color: "#fecaca", fontWeight: 600 }}>{pdfViewer.error}</div>
              ) : null}
              {!pdfViewer.loading && pdfViewer.url ? (
                <iframe title="Aperçu du rapport PDF" src={pdfViewer.url} style={{ width: "100%", height: "72vh", border: "none", display: "block", background: "#334155" }} />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {archiveCandidate ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-archive-dialog-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => e.target === e.currentTarget && !archivingId && setArchiveCandidate(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 440,
              width: "100%",
              padding: "22px 24px",
              boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="report-archive-dialog-title" style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
              Archiver ce rapport&nbsp;?
            </h2>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#475569", lineHeight: 1.55 }}>
              Le rapport <strong>{archiveCandidate.title || `#${archiveCandidate.id}`}</strong> sera retiré de la liste principale. Vous pourrez le consulter via « Rapports archivés » (téléchargement et preuve restent disponibles).
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                disabled={Boolean(archivingId)}
                onClick={() => setArchiveCandidate(null)}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: archivingId ? "wait" : "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={Boolean(archivingId)}
                onClick={handleArchiveConfirmed}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#64748b",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: archivingId ? "wait" : "pointer",
                  opacity: archivingId ? 0.85 : 1,
                }}
              >
                {archivingId ? "Archivage…" : "Archiver"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ProofModal
        details={proofDetails}
        reportId={proofReportId}
        currentUser={currentUser}
        onToast={onToast}
        onRefresh={async () => {
          await loadReports();
          if (proofReportId) {
            const data = await reportsApi.proof(proofReportId);
            setProofDetails(data);
          }
        }}
        onClose={handleCloseProof}
      />
      </PageContent>
    </PageShell>
  );
}
